import { useContext } from "react";

import { useTranslation } from "react-i18next";

import AuthContext, { PERMISSIONS } from "context/auth/authContext";

const usePermissions = () => {
    const { permissions } = useContext(AuthContext);

    const { t } = useTranslation();

    // Devices
    const isReadDeviceNotAllowed = !permissions[PERMISSIONS.DeviceRead];
    const isCreateTaskNotAllowed = !permissions[PERMISSIONS.TaskCreate];
    const isUpdateDeviceNotAllowed = !permissions[PERMISSIONS.DeviceUpdate];
    const isExportDeviceNotAllowed = !permissions[PERMISSIONS.DeviceExport];
    const isTransferDeviceNotAllowed = !permissions[PERMISSIONS.DeviceTransfer];
    const isUnlockDeviceNotAllowed = !permissions[PERMISSIONS.DeviceUnlock];
    const isSecretsReadDeviceNotAllowed =
        !permissions[PERMISSIONS.DeviceSecretsRead];

    // Task groups
    const isReadTaskGroupNotAllowed = !permissions[PERMISSIONS.TaskGroupRead];

    // Tasks
    const isReadTaskNotAllowed = !permissions[PERMISSIONS.TaskRead];
    const isRetryTaskNotAllowed = !permissions[PERMISSIONS.TaskRetry];
    const isUpdateTaskNotAllowed = !permissions[PERMISSIONS.TaskUpdate];

    // Device groups
    const isReadDeviceGroupNotAllowed =
        !permissions[PERMISSIONS.DeviceGroupRead];

    const isUpdateDeviceGroupNotAllowed =
        !permissions[PERMISSIONS.DeviceGroupUpdate];

    const isCreateDeviceGroupNotAllowed =
        !permissions[PERMISSIONS.DeviceGroupCreate];

    // Files
    const isReadFileNotAllowed = !permissions[PERMISSIONS.FileRead];
    const isCreateFileNotAllowed = !permissions[PERMISSIONS.FileCreate];
    const isEditInTctWebNotAllowed = !permissions[PERMISSIONS.FileTctWebAccess];
    const isDownloadFileNotAllowed = !permissions[PERMISSIONS.FileDownload];

    // Companies
    const isCreateCompanyNotAllowed = !permissions[PERMISSIONS.CompanyCreate];
    const isUpdateCompanyNotAllowed = !permissions[PERMISSIONS.CompanyUpdate];
    const isMergeCompanyNotAllowed = !permissions[PERMISSIONS.CompanyMerge];

    // Users
    const isReadUserNotAllowed = !permissions[PERMISSIONS.UserRead];
    const isCreateUserNotAllowed = !permissions[PERMISSIONS.UserCreate];

    // Roles
    const isReadRoleNotAllowed = !permissions[PERMISSIONS.RoleRead];
    const isCreateRoleNotAllowed = !permissions[PERMISSIONS.RoleCreate];

    // Tokens
    const isReadTokenNotAllowed = !permissions[PERMISSIONS.TokenRead];
    const isCreateTokenNotAllowed = !permissions[PERMISSIONS.TokenCreate];
    const isDeleteTokenNotAllowed = !permissions[PERMISSIONS.TokenDelete];

    const getNoPermissionTooltipTitle = (
        permissionName: PERMISSIONS | PERMISSIONS[]
    ) => {
        const permissionsArray = Array.isArray(permissionName)
            ? permissionName
            : [permissionName];

        const missingPermissions = permissionsArray.filter(
            name => !permissions[name]
        );

        const hasMultipleMissingPermissions = missingPermissions.length > 1;

        if (hasMultipleMissingPermissions) {
            const listOfTranslatedPermissions = missingPermissions
                .map(name => {
                    const translatedName = t(`General##${name}`);
                    return `"${translatedName}"`;
                })
                .join(", ");

            return t("Button##no permissions", {
                permissionNames: listOfTranslatedPermissions
            });
        }

        const missingPermission = missingPermissions[0];

        return missingPermission !== undefined
            ? t("Button##no permission", {
                  permissionName: t(`General##${missingPermission}`)
              })
            : undefined;
    };

    return {
        isReadDeviceNotAllowed,
        isCreateTaskNotAllowed,
        isUpdateDeviceNotAllowed,
        isExportDeviceNotAllowed,
        isTransferDeviceNotAllowed,
        isUnlockDeviceNotAllowed,
        isSecretsReadDeviceNotAllowed,
        isReadTaskGroupNotAllowed,
        isReadTaskNotAllowed,
        isRetryTaskNotAllowed,
        isUpdateTaskNotAllowed,
        isReadDeviceGroupNotAllowed,
        isUpdateDeviceGroupNotAllowed,
        isCreateDeviceGroupNotAllowed,
        isReadFileNotAllowed,
        isCreateFileNotAllowed,
        isEditInTctWebNotAllowed,
        isDownloadFileNotAllowed,
        isCreateCompanyNotAllowed,
        isUpdateCompanyNotAllowed,
        isMergeCompanyNotAllowed,
        isReadUserNotAllowed,
        isCreateUserNotAllowed,
        isReadRoleNotAllowed,
        isCreateRoleNotAllowed,
        isReadTokenNotAllowed,
        isCreateTokenNotAllowed,
        isDeleteTokenNotAllowed,
        permissions,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    };
};

export { usePermissions };
